import Reveal from "@/components/Reveal";
import { CHAPTER_II_URL } from "@/lib/the190";
import { trackOutbound } from "@/lib/analytics";

const SUPPORT_IMAGES = [
  { src: "/chapter2/d3434.webp", caption: "Original paintings · opening night" },
  { src: "/chapter2/d3654.webp", caption: "Opening night · Shoreditch Arts Club" },
  { src: "/chapter2/d3459.webp", caption: "Film screening · collection overview" },
];

/* -------------------------- BORN FROM CHAPTER II -------------------------- */

export const ChapterArchive190 = () => (
  <section
    id="chapter-ii"
    data-testid="the190-chapter-archive"
    className="dark-section py-28 md:py-44"
    style={{ scrollMarginTop: 80 }}
  >
    <div className="max-w-[1600px] mx-auto px-6 md:px-12">
      {/* Cinematic lead image */}
      <Reveal slow>
        <figure className="mb-16 md:mb-24">
          <img
            src="/hero/projection.jpg"
            alt="Chapter II — the Nick Fraser exhibition at Shoreditch Arts Club, immersive projection during the opening night"
            className="w-full h-auto block"
            loading="lazy"
            data-testid="the190-chapter-hero-image"
          />
          <figcaption className="hairline opacity-55 mt-4">
            Chapter II · Shoreditch Arts Club · 31 July 2026
          </figcaption>
        </figure>
      </Reveal>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
        <div className="md:col-span-5">
          <Reveal slow>
            <p className="hairline opacity-60 mb-6">Provenance</p>
            <h2 className="font-wordmark text-5xl md:text-7xl leading-[1.02]">
              Born from Chapter II.
            </h2>
            <p className="hairline opacity-55 mt-8">
              Shoreditch Arts Club · London · 31 July 2026
            </p>
          </Reveal>
        </div>
        <Reveal className="md:col-span-7" delay={140}>
          <p className="font-serif italic text-xl md:text-2xl leading-[1.45] opacity-90">
            The Collector Editions were introduced through Chapter II — a public
            presentation that brought together Nick Fraser&apos;s original paintings,
            documentary film, archival material and immersive projection.
          </p>
          <p className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-70 mt-6">
            The editions belong to a real exhibition, a growing archive and an
            evolving artistic legacy — not to a standalone commercial print run.
          </p>
          <a
            href={CHAPTER_II_URL}
            onClick={() => trackOutbound("chapter-ii")}
            className="hairline underline underline-offset-8 opacity-80 hover:opacity-100 transition-opacity duration-500 inline-block mt-10"
            data-testid="the190-chapter-ii-link"
          >
            Discover Chapter II →
          </a>
        </Reveal>
      </div>

      {/* Restrained supporting gallery — 3 images only */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10 mt-20 md:mt-28">
        {SUPPORT_IMAGES.map((img, i) => (
          <Reveal key={img.src} delay={i * 90} slow>
            <figure data-testid={`the190-chapter-support-${i}`}>
              <img
                src={img.src}
                alt={`Chapter II — ${img.caption}`}
                loading="lazy"
                className="w-full h-auto block aspect-[4/3] object-cover"
              />
              <figcaption className="hairline opacity-50 mt-3">{img.caption}</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* -------------------------- FROM THE ARCHIVE · 1996 -------------------------- */

export const FromArchive1996 = () => (
  <section
    id="from-archive"
    data-testid="the190-from-archive"
    className="bone-section py-28 md:py-44"
    style={{ scrollMarginTop: 80 }}
  >
    <div className="max-w-[1200px] mx-auto px-6 md:px-12">
      <Reveal slow>
        <p className="hairline opacity-60 mb-6">From the Archive</p>
        <h2 className="font-wordmark text-6xl md:text-8xl leading-[1.02]">1996.</h2>
      </Reveal>
      <div className="mt-12 max-w-3xl space-y-6">
        <Reveal delay={80}>
          <p className="font-serif italic text-xl md:text-2xl leading-[1.45] opacity-85">
            Both Tribal Goddess and Universal Soul of Stars originate from this
            period in Nick Fraser&apos;s work — a moment when his visual language of
            line, colour and figure emerged with unusual clarity.
          </p>
        </Reveal>
        <Reveal delay={140}>
          <p className="font-serif italic text-lg md:text-xl leading-[1.55] opacity-75">
            It is also the year Nick was first mentioned in Raw Vision — an early
            recognition of the significance of his practice, decades before his
            work reached a public exhibition.
          </p>
        </Reveal>
      </div>
      <Reveal className="mt-14" slow>
        <a
          href="https://www.nickfraserartist.com/nick-fraser-artist"
          onClick={() => trackOutbound("archive-explore")}
          className="hairline underline underline-offset-8 opacity-80 hover:opacity-100 transition-opacity duration-500 inline-block"
          data-testid="the190-archive-link"
        >
          Explore the Nick Fraser Archive →
        </a>
      </Reveal>
    </div>
  </section>
);

export default ChapterArchive190;
